import React from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import Panel from "../../components/Panel";

const CARD_STYLE = {
  boxShadow: "none",
  border: `1px solid ${T.border}`,
  borderRadius: 12,
  height: 560,
  boxSizing: "border-box",
  overflowY: "auto",
};

const useStyles = makeStyles({
  panel: {
    padding: "16px 20px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  eyebrow: {
    fontSize: 11,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: T.subtleFg,
    fontWeight: 600,
  },
  total: {
    fontSize: 12,
    color: T.mutedFg,
    fontVariantNumeric: "tabular-nums",
  },
  lot: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    border: "none",
    background: "transparent",
    padding: 0,
    width: "100%",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "inherit",
    borderRadius: 6,
    "&:hover": { background: T.surface },
    "&:focus-visible": {
      outline: `2px solid ${T.primary}`,
      outlineOffset: 2,
    },
  },
  line: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 13,
    color: T.fg,
  },
  count: {
    color: T.mutedFg,
    fontVariantNumeric: "tabular-nums",
  },
  track: {
    height: 6,
    borderRadius: 999,
    background: T.muted,
    overflow: "hidden",
  },
  fill: { height: "100%", background: T.primary, borderRadius: 999 },
  fillOffsite: { opacity: 0.55 },
});

export default function ParkedByLotPanel({ lots = [], loading, error, onOpenLot }) {
  const s = useStyles();
  const capacity = lots.every((lot) => Number.isFinite(lot.capacity))
    ? lots.reduce((total, lot) => total + lot.capacity, 0)
    : null;
  const occupied = lots.reduce((total, lot) => total + lot.occupied, 0);

  return (
    <Panel className={s.panel} style={CARD_STYLE} aria-label="Parked by lot">
      <div className={s.head}>
        <span className={s.eyebrow}>Parked by lot</span>
        <span className={s.total}>
          {occupied} / {capacity ?? "-"} parked
        </span>
      </div>

      {loading && lots.length === 0 && (
        <span className={s.total}>Loading lots…</span>
      )}
      {error && lots.length === 0 && (
        <span className={s.total}>Could not load lot occupancy.</span>
      )}
      {!loading && !error && lots.length === 0 && (
        <span className={s.total}>No parking lots configured.</span>
      )}

      {lots.map((lot) => {
        const ratio = lot.capacity > 0
          ? Math.min(1, lot.occupied / lot.capacity)
          : 0;

        return (
          <button
            key={lot.id}
            type="button"
            className={s.lot}
            onClick={() => onOpenLot(lot)}
            aria-label={`Open ${lot.name}`}
          >
            <div className={s.line}>
              <span>{lot.name}</span>
              <span className={s.count}>
                {lot.occupied} / {lot.capacity ?? "-"}
              </span>
            </div>
            <div
              className={s.track}
              role="img"
              aria-label={`${lot.name} ${lot.occupied} of ${lot.capacity ?? "unknown capacity"} parked`}
            >
              <div
                className={`${s.fill} ${
                  lot.type === "OFFSITE" ? s.fillOffsite : ""
                }`}
                style={{ width: `${ratio * 100}%` }}
              />
            </div>
          </button>
        );
      })}
    </Panel>
  );
}
